import { APP_BASE_HREF, CommonModule, TitleCasePipe } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
  withJsonpSupport,
  withXhr,
} from '@angular/common/http';
import {
  APP_ID,
  inject,
  LOCALE_ID,
  NgModule,
  provideAppInitializer,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { AnalyticsGuard } from '@app/analytics/guards/analytics.guard';
import { initAppState } from '@app/app-initializer';
import { MsTeamsBootstrapComponent } from '@app/ms-teams/components/ms-teams-bootstrap/ms-teams-bootstrap.component';
import {
  MSTeamsMgtToken,
  teamsMgtTokenProvider,
} from '@app/ms-teams/msteams-mgt.token';
import {
  MSTeamsMsal2Token,
  teamsMsal2TokenProvider,
} from '@app/ms-teams/msteams-msal2.token';
import {
  microsoftTeamsProvider,
  MicrosoftTeamsToken,
} from '@app/ms-teams/msteams.token';
import { MsTeamsAuthInterceptor } from '@app/ms-teams/services/ms-teams-auth/ms-teams-auth.interceptor';
import { SharedProvidersModule } from '@app/shared/shared-providers.module';

import { UserContentModule } from '@app/user-content/user-content.module';
import {
  ApplicationId,
  BrandingService,
  CachingInterceptor,
  ConfigurationService,
  DgTranslateDynamicLoader,
  LDFlagsService,
  LocalizationService,
  MiniProfilerInterceptor,
  NgxHttpClient,
  ORG_TYPE_TOKEN,
  VendorIntegrationsService,
  WebEnvironmentService,
  WindowToken,
  XSRFTokenInterceptor,
} from '@degreed/lxp/services';
import {
  AuthInterceptor,
  AuthService,
} from '@degreed/lxp/services/authorization';
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
@NgModule({
  declarations: [MsTeamsBootstrapComponent],
  bootstrap: [MsTeamsBootstrapComponent],
  imports: [
    BrowserModule,
    CommonModule,
    BrowserAnimationsModule,
    UserContentModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: DgTranslateDynamicLoader,
      },
    }),
    SharedProvidersModule,
    RouterModule.forRoot([
      {
        path: 'degreed-ms-teams/app',
        loadChildren: () =>
          import('./ms-teams.module').then((m) => m.MsTeamsModule),
      },
    ]),
  ],
  providers: [
    // translation
    {
      provide: ORG_TYPE_TOKEN,
      useFactory: (authService: AuthService) => () =>
        authService.authUser?.defaultOrgInfo?.type,
      deps: [AuthService],
    },
    // interceptors
    { provide: HTTP_INTERCEPTORS, useClass: XSRFTokenInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: CachingInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MiniProfilerInterceptor,
      multi: true,
    },
    TitleCasePipe,
    NgxHttpClient,
    AnalyticsGuard,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsTeamsAuthInterceptor,
      multi: true,
    },
    { provide: MicrosoftTeamsToken, useFactory: microsoftTeamsProvider },
    { provide: MSTeamsMsal2Token, useFactory: teamsMsal2TokenProvider },
    { provide: MSTeamsMgtToken, useFactory: teamsMgtTokenProvider },
    {
      provide: LOCALE_ID,
      deps: [LocalizationService],
      useFactory: (localizationService: LocalizationService) => {
        const { userLocale } = localizationService;
        // lazy-load the locales which we need for localizing dates in some areas of the app
        localizationService.loadLocale(userLocale);
        return userLocale;
      },
    },
    {
      provide: APP_BASE_HREF,
      useFactory: (win: Window) => win.origin,
      deps: [WindowToken],
    },
    provideAppInitializer(() => {
      const initializerFn = initAppState(
        inject(WebEnvironmentService),
        inject(AuthService),
        inject(TranslateService),
        inject(ConfigurationService),
        inject(LocalizationService),
        inject(LDFlagsService),
        inject(BrandingService),
        inject(VendorIntegrationsService)
      );
      return initializerFn();
    }),
    {
      provide: APP_ID,
      useValue: ApplicationId.TeamsApp,
    },
    provideHttpClient(withXhr(), withInterceptorsFromDi(), withJsonpSupport()),
  ],
})
export class AppMsTeamsBootstrapModule {}
